import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import Button from '../../components/elements/button';
import FileUpload from '../../components/elements/file_upload';
import EmailFirstAuth from '../../components/auth/EmailFirstAuth';
import { CircleCheck, Map } from '../../assets/icons';
import '../../assets/css/doctor/register.css';
import { doctorApi } from '../../network/apis/doctors';
import { saveDraft, loadDraft, clearDraft, getDraftAge } from '../../utils/registrationDraft';

const DoctorRegister = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [draftAge, setDraftAge] = useState(null);
    
    const [formData, setFormData] = useState({
        // Professional Details
        license_number: '',
        specialization: '',
        experience: '',
        qualifications: [],
        certifications: [],

        // Practice Details
        clinic_name: '',
        clinic_address: '',
        longitude:'',
        latitude: '',
        working_hours_from: '',
        working_hours_to: '',
        consultation_fee: '',

        // Documents
        license_document: null,
        identity_document: null
    });

    const validateSpecialization = (specialization) => {
        const lowerSpecialization = specialization.toLowerCase();
        return lowerSpecialization.includes('aquatic') || 
               lowerSpecialization.includes('fish') ||
               lowerSpecialization.includes('aqua');
    };

    const totalSteps = 4; // Account, Professional, Practice, Documents

    // Check for existing draft on mount
    useEffect(() => {
        const draft = loadDraft('doctor');
        if (draft && (draft.data.license_number || draft.data.clinic_name)) {
            setDraftAge(getDraftAge('doctor'));
            setShowDraftModal(true);
        }
    }, []);
    
    // Auto-save when form data changes (only for non-auth steps)
    useEffect(() => {
        if (currentStep > 1 && (formData.license_number || formData.clinic_name)) {
            saveDraft('doctor', formData, currentStep);
        }
    }, [formData, currentStep]);
    
    const handleRestoreDraft = () => {
        const draft = loadDraft('doctor');
        if (draft) {
            setFormData(prev => ({ ...prev, ...draft.data }));
            // Only restore to step 2+ if authenticated
            if (isAuthenticated && draft.currentStep > 1) {
                setCurrentStep(draft.currentStep);
            }
            setSuccess('Draft restored successfully!');
        }
        setShowDraftModal(false);
    };
    
    const handleDiscardDraft = () => {
        clearDraft('doctor');
        setShowDraftModal(false);
    };

    // Handle authentication completion from EmailFirstAuth component
    const handleAuthComplete = (userData) => {
        setIsAuthenticated(true);
        
        // Check if user is already a registered doctor
        if (userData?.is_doctor) {
            setSuccess('You are already registered as a veterinarian! Redirecting...');
            setTimeout(() => navigate('/vet'), 1500);
            return;
        }
        
        // Pre-fill any available user data
        if (userData) {
            setFormData(prev => ({
                ...prev,
                email: userData.email || '',
                firstName: userData.firstName || userData.first_name || '',
                lastName: userData.lastName || userData.last_name || '',
                phone: userData.phone || ''
            }));
        }
        
        // Move to next step
        setCurrentStep(2);
    };

    const geocodeTimeoutRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const googleScriptLoadedRef = useRef(false);
    
    // Add state for address lookup
    const [addressLookup, setAddressLookup] = useState({
        loading: false,
        options: [],
        error: null
    });

    // Load Google Maps JavaScript SDK
    useEffect(() => {
        const loadGoogleMapsScript = () => {
            if (window.google?.maps?.places) {
                // Already loaded
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                placesServiceRef.current = new window.google.maps.places.PlacesService(
                    document.createElement('div')
                );
                googleScriptLoadedRef.current = true;
                return;
            }

            if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                // Script is loading, wait for it
                const checkGoogle = setInterval(() => {
                    if (window.google?.maps?.places) {
                        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                        placesServiceRef.current = new window.google.maps.places.PlacesService(
                            document.createElement('div')
                        );
                        googleScriptLoadedRef.current = true;
                        clearInterval(checkGoogle);
                    }
                }, 100);
                return;
            }

            const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCEBU9rIpanT0nUeCI3RWjKLyVmfKG7-x8';
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                placesServiceRef.current = new window.google.maps.places.PlacesService(
                    document.createElement('div')
                );
                googleScriptLoadedRef.current = true;
            };
            document.head.appendChild(script);
        };

        loadGoogleMapsScript();
    }, []);

    // Geocode function using Google Places Autocomplete (via JavaScript SDK)
    const geocodeAddress = async (address) => {
        if (!address.trim() || address.length < 3) {
            return;
        }
        
        setAddressLookup(prev => ({ ...prev, loading: true, error: null }));
        
        // Try Google Places first if loaded
        if (googleScriptLoadedRef.current && autocompleteServiceRef.current) {
            try {
                autocompleteServiceRef.current.getPlacePredictions(
                    {
                        input: address,
                        types: ['geocode', 'establishment']
                    },
                    (predictions, status) => {
                        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                            // Get details for each prediction to get coordinates
                            const detailPromises = predictions.slice(0, 5).map(prediction => {
                                return new Promise((resolve) => {
                                    placesServiceRef.current.getDetails(
                                        {
                                            placeId: prediction.place_id,
                                            fields: ['geometry', 'formatted_address']
                                        },
                                        (place, detailStatus) => {
                                            if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                                                resolve({
                                                    address: place.formatted_address || prediction.description,
                                                    lat: place.geometry.location.lat().toString(),
                                                    lon: place.geometry.location.lng().toString()
                                                });
                                            } else {
                                                resolve(null);
                                            }
                                        }
                                    );
                                });
                            });

                            Promise.all(detailPromises).then(results => {
                                const validResults = results.filter(r => r !== null);
                                setAddressLookup(prev => ({
                                    ...prev,
                                    loading: false,
                                    options: validResults
                                }));
                            });
                        } else {
                            // Fall back to LocationIQ
                            fallbackGeocode(address);
                        }
                    }
                );
                return;
            } catch (googleError) {
                console.warn("Google Places error, falling back:", googleError);
            }
        }
        
        // Fallback if Google not loaded
        fallbackGeocode(address);
    };

    // Fallback geocoding using LocationIQ
    const fallbackGeocode = async (address) => {
        try {
            const LOCATIONIQ_KEY = 'pk.0f147952a41c555a5b70a3d7b8d77f66';
            const response = await fetch(
                `https://api.locationiq.com/v1/autocomplete?key=${LOCATIONIQ_KEY}&q=${encodeURIComponent(address)}&limit=5&dedupe=1&normalizecity=1`
            );
            
            if (response.ok) {
                const data = await response.json();
                if (data && data.length > 0) {
                    setAddressLookup(prev => ({
                        ...prev,
                        loading: false,
                        options: data.map(item => ({
                            address: item.display_name,
                            lat: item.lat,
                            lon: item.lon
                        }))
                    }));
                    return;
                }
            }
            
            setAddressLookup(prev => ({
                ...prev,
                loading: false,
                options: [],
                error: "No locations found. Try a more specific address."
            }));
        } catch (error) {
            console.error("Geocoding error:", error);
            setAddressLookup(prev => ({
                ...prev,
                loading: false,
                error: "Failed to look up address. Please try again."
            }));
        }
    };

    // Handle address change with debounce
    const handleAddressChange = (e) => {
        const address = e.target.value;
        setFormData({...formData, clinic_address: address});
        
        // Clear previous timeout
        if (geocodeTimeoutRef.current) {
            clearTimeout(geocodeTimeoutRef.current);
        }
        
        // Clear suggestions if input is too short
        if (address.length < 3) {
            setAddressLookup({
                loading: false,
                options: [],
                error: null
            });
            return;
        }
        
        // Set new timeout for geocoding (500ms for faster response)
        geocodeTimeoutRef.current = setTimeout(() => {
            geocodeAddress(address);
        }, 500);
    };

    // Select an address from suggestions
    const selectAddress = (option) => {
        setFormData({
            ...formData,
            clinic_address: option.address,
            latitude: parseFloat(option.lat).toFixed(6),
            longitude: parseFloat(option.lon).toFixed(6)
        });
        setAddressLookup(prev => ({...prev, options: []}));
    };

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (geocodeTimeoutRef.current) {
                clearTimeout(geocodeTimeoutRef.current);
            }
        };
    }, []);

    // Handle file upload with the new FileUpload component
    const handleFileUpload = (field, file) => {
        if (file === null) {
            setFormData(prev => ({
                ...prev,
                [field]: null
            }));
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: file
        }));
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        setError(null);
        setSuccess(null);
        
        if (!formData.latitude || !formData.longitude) {
            setError('Please provide location coordinates. They should be automatically populated when you select an address.');
            setCurrentStep(3); // Go back to practice details
            return;
        }
        // Validate specialization contains required terms
        if (!validateSpecialization(formData.specialization)) {
            setError('Specialization must include "aquatic", "fish", or "aqua" terms. This platform is for aquatic veterinarians only.');
            // Set current step to professional details to fix the issue
            setCurrentStep(2);
            return;
        }

        // Check if files are selected
        if (!formData.license_document) {
            setError('Please upload your license document');
            setCurrentStep(4);
            return;
        }
        
        if (!formData.identity_document) {
            setError('Please upload your identity document');
            setCurrentStep(4);
            return;
        }
        
        setIsSubmitting(true);
        try {
            // Create a new FormData object for submission
            const formDataForSubmit = new FormData();
            
            // Append all text fields
            Object.keys(formData).forEach(key => {
                if (key !== 'license_document' && 
                    key !== 'identity_document' &&
                    formData[key] !== null &&
                    formData[key] !== undefined) {
                    formDataForSubmit.append(key, formData[key]);
                }
            });
            
            // Append files directly
            formDataForSubmit.append('license_document', formData.license_document);
            formDataForSubmit.append('identity_document', formData.identity_document);
            
            // Submit doctor registration data
            await doctorApi.newDoctor(formDataForSubmit);
            
            // Clear draft on successful registration
            clearDraft('doctor');
            
            setSuccess('Registration submitted successfully. Your application is under review.');
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            // Extract error message from backend response
            let errorMessage = 'Failed to submit registration';
            
            if (err.response?.data) {
                const errorData = err.response.data;
                // Check for errors array first
                if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                    errorMessage = errorData.errors[0];
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
            } else if (err.displayMessage) {
                errorMessage = err.displayMessage;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Step navigation function
    const handleStepNavigation = (direction) => {
        setError(null);
        setSuccess(null);
        
        // Prevent going back from step 2 if authenticated (can't change auth)
        if (direction === 'previous' && currentStep === 2 && isAuthenticated) {
            return;
        }
        
        if (direction === 'next') {
            // Step 2 validation - Professional details
            if (currentStep === 2) {
                if (!formData.license_number.trim()) {
                    setError('Please enter your license number');
                    return;
                }
                if (!validateSpecialization(formData.specialization)) {
                    setError('Specialization must include "aquatic", "fish", or "aqua". This platform is for aquatic veterinarians only.');
                    return;
                }
            }
            
            // Step 3 validation - Practice details
            if (currentStep === 3) {
                if (!formData.clinic_name.trim()) {
                    setError('Please enter your workplace name');
                    return;
                }
                if (!formData.clinic_address.trim()) {
                    setError('Please enter your clinic address');
                    return;
                }
            }
            
            // Step 4 - Submit
            if (currentStep === 4) {
                // Trigger form submission
                document.querySelector('.registration-form')?.requestSubmit();
                return;
            }
        }
        
        setCurrentStep(prev => direction === 'next' ? prev + 1 : prev - 1);
    };

    return (
        <div className="doctor-register">
            {/* Draft Restore Modal */}
            {showDraftModal && isAuthenticated && (
                <div className="draft-modal-overlay">
                    <div className="draft-modal">
                        <div className="draft-modal-icon">📝</div>
                        <h3>Continue where you left off?</h3>
                        <p>We found a saved draft from {draftAge}. Would you like to continue with your previous progress?</p>
                        <div className="draft-modal-actions">
                            <Button 
                                text="Start Fresh" 
                                variant="outline" 
                                onClick={handleDiscardDraft}
                            />
                            <Button 
                                text="Restore Draft" 
                                variant="primary" 
                                onClick={handleRestoreDraft}
                            />
                        </div>
                    </div>
                </div>
            )}
            
            <div className="register-container">
                <div className="register-header">
                    <h1>Veterinarian Registration</h1>
                    <p>Join our network of aquaculture health specialists</p>
                </div>

                <div className="progress-tracker">
                    
                    {[...Array(4)].map((_, index) => (
                        <div 
                            key={index}
                            className={`progress-step ${currentStep > index + 1 ? 'completed' : ''} 
                                      ${currentStep === index + 1 ? 'active' : ''}`}
                        >
                            <div className="step-indicator">
                                {currentStep > index + 1 ? <img src={CircleCheck} className="step-indicator-icon" alt="completed"/> : index + 1}
                            </div>
                            <span className="step-label">
                                {['Account', 'Professional', 'Practice', 'Documents'][index]}
                            </span>
                            <span className="step-description">
                                {['Login or Register', 'Your Expertise', 'Work Details', 'Verification'][index]}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Error/Success Alert */}
                {error && (
                    <div className="alert alert-error">
                        <span className="alert-icon">⚠️</span>
                        <span className="alert-message">{error}</span>
                        <button type="button" className="alert-close" onClick={() => setError(null)}>×</button>
                    </div>
                )}
                {success && (
                    <div className="alert alert-success">
                        <span className="alert-icon">✓</span>
                        <span className="alert-message">{success}</span>
                        <button type="button" className="alert-close" onClick={() => setSuccess(null)}>×</button>
                    </div>
                )}

                    {/* <form action="#" onSubmit={handleSubmit} className="registration-form"> */}
                       <div className="registration-form">
                        {currentStep === 1 && (
                            <div className="form-step">
                                <div className="step-header">
                                    <h2>Create Your Account</h2>
                                    <p className="step-subtitle">
                                        Start your journey as a certified aquatic veterinarian
                                    </p>
                                </div>
                                
                                <EmailFirstAuth 
                                    onAuthSuccess={handleAuthComplete}
                                    userType="doctor"
                                />
                            </div>
                        )}

                    {currentStep === 2 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Professional Details</h2>
                                <p className="step-subtitle">Tell us about your qualifications and expertise</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="input-grid">
                                    <FormInput
                                        label="License Number"
                                        value={formData.license_number}
                                        onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                                        required
                                        showLabel
                                        placeholder="e.g., VET-2024-XXXX"
                                    />
                                    <FormInput
                                        label="Specialization"
                                        value={formData.specialization}
                                        onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                                        required
                                        showLabel
                                        placeholder="e.g., Aquatic Veterinary Medicine"
                                        helpText="Must include 'aquatic', 'fish', or 'aqua'"
                                    />
                                    <FormInput
                                        label="Years of Experience"
                                        type="number"
                                        value={formData.experience}
                                        onChange={(e) => setFormData({...formData, experience: e.target.value})}
                                        required
                                        showLabel
                                        placeholder="e.g., 5"
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Practice Details</h2>
                                <p className="step-subtitle">Tell us about your workplace and availability</p>
                            </div>
                            
                            <div className="form-section">
                                <FormInput
                                    label="Name of Workplace"
                                    value={formData.clinic_name}
                                    showLabel
                                    required
                                    onChange={(e) => setFormData({...formData, clinic_name: e.target.value})}
                                    placeholder="e.g., Aquatic Veterinary Clinic"
                                />
                                
                                <div className="address-input-container">
                        <FormInput
                            label="Address"
                            value={formData.clinic_address}
                            showLabel
                            required
                            onChange={handleAddressChange}
                            icon={<img src={Map} alt="Location" />}
                            helpText="Type to search address"
                        />
                        
                        {addressLookup.loading && (
                            <div className="address-loading">
                                <div className="address-spinner"></div>
                                <span>Searching addresses...</span>
                            </div>
                        )}
                        
                        {addressLookup.options.length > 0 && (
                            <div className="address-suggestions">
                                {addressLookup.options.map((option, index) => (
                                    <div 
                                        key={index}
                                        className="address-option"
                                        onClick={() => selectAddress(option)}
                                    >
                                        <span className="address-text">{option.address}</span>
                                        <span className="address-coords">
                                            {parseFloat(option.lat).toFixed(6)}, {parseFloat(option.lon).toFixed(6)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {addressLookup.error && (
                            <div className="address-error">
                                {addressLookup.error}
                            </div>
                        )}
                    </div>
                    
                    <div className="coordinates-display">
                        <div className="coordinate-field">
                            <label>Latitude</label>
                            <div className="coordinate-value-container">
                                <input 
                                    type="text"
                                    value={formData.latitude || ""}
                                    onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                                    readOnly={!!formData.latitude}
                                    placeholder="Set automatically from address"
                                />
                                {formData.latitude && (
                                    <button 
                                        type="button" 
                                        className="clear-coordinate"
                                        onClick={() => setFormData({...formData, latitude: ''})}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div className="coordinate-field">
                            <label>Longitude</label>
                            <div className="coordinate-value-container">
                                <input 
                                    type="text"
                                    value={formData.longitude || ""}
                                    onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                                    readOnly={!!formData.longitude}
                                    placeholder="Set automatically from address"
                                />
                                {formData.longitude && (
                                    <button 
                                        type="button" 
                                        className="clear-coordinate"
                                        onClick={() => setFormData({...formData, longitude: ''})}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                                <div className="working-hours">
                                    <label>Working Hours</label>
                                    <div className="hours-inputs">
                                        <input 
                                            type="time"
                                            value={formData.working_hours_from}
                                            onChange={(e) => setFormData({
                                                ...formData, working_hours_from: e.target.value
                                            })}
                                        />
                                        <span>to</span>
                                        <input 
                                            type="time"
                                            value={formData.working_hours_to}
                                            onChange={(e) => setFormData({
                                                ...formData, working_hours_to: e.target.value
                                            })}
                                        />
                                    </div>
                                </div>
                                <div className="consultation-fee-section">
                                    <label className="fee-label">
                                        <span className="fee-icon">💰</span>
                                        Consultation Fee (NGN)
                                        <span className="fee-badge">Starting from</span>
                                    </label>
                                    <div className="fee-input-wrapper">
                                        <span className="currency-prefix">₦</span>
                                        <input
                                            type="number"
                                            className="fee-input"
                                            value={formData.consultation_fee}
                                            onChange={(e) => setFormData({...formData, consultation_fee: e.target.value})}
                                            placeholder="5,000"
                                            min="0"
                                            required
                                        />
                                    </div>
                                    <p className="fee-hint">Set your minimum consultation charge for clients</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {currentStep === 4 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Document Verification</h2>
                                <p className="step-subtitle">Upload your professional documents for verification</p>
                            </div>
                            
                            <div className="document-uploads">
                                <FileUpload
                                    label="License Document"
                                    field="license_document"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onUpload={handleFileUpload}
                                    currentFile={formData.license_document}
                                    required={true}
                                    hint="Your veterinary license certificate (PDF, JPG, PNG - max 15MB)"
                                    disabled={isSubmitting}
                                />
                                
                                <FileUpload
                                    label="Identity Document"
                                    field="identity_document"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onUpload={handleFileUpload}
                                    currentFile={formData.identity_document}
                                    required={true}
                                    hint="Government-issued ID - passport, driver's license, etc. (max 15MB)"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-navigation">
                        {currentStep > 1 && !isAuthenticated && (
                            <Button
                                text="Previous"
                                variant="outline"
                                onClick={() => handleStepNavigation('previous')}
                                disabled={isSubmitting}
                            />
                        )}
                        {currentStep > 2 && isAuthenticated && (
                            <Button
                                text="Previous"
                                variant="outline"
                                onClick={() => handleStepNavigation('previous')}
                                disabled={isSubmitting}
                            />
                        )}
                        
                        {/* Step 1 has no button - EmailFirstAuth handles navigation */}
                        {currentStep > 1 && currentStep < totalSteps && (
                            <Button
                                text="Continue"
                                variant="primary"
                                onClick={() => handleStepNavigation('next')}
                            />
                        )}
                        
                        {currentStep === totalSteps && (
                            <Button
                                text={isSubmitting ? 'Submitting...' : 'Submit Application'}
                                // type="submit"
                                onClick={() => handleSubmit()}
                                variant="primary"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            />
                        )}
                    </div>
                {/* </form> */}
                </div>
            </div>
        </div>
    );
};

export default DoctorRegister;